import { Component, EventEmitter, Output } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { ScreeningDocType } from '../../microservice-clients/tutor-screening.model';
import { TutorScreeningService } from '../../services/tutor-screening.service';

@Component({
  selector: 'app-screening-upload-dialog',
  standalone: true,
  imports: [TranslateModule, MatProgressSpinnerModule],
  templateUrl: './screening-upload-dialog.component.html',
  styleUrls: ['./screening-upload-dialog.component.scss'],
})
export class ScreeningUploadDialogComponent {
  docTypes: ScreeningDocType[] = [];
  /** Reminder variant for live DE tutors (SCRUM-797): own copy and a "later" button. Set by TutorCertificateReminderService. */
  reminder = false;

  @Output() onConfirm = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  private static readonly MAX_FILE_BYTES = 10 * 1024 * 1024;

  readonly DocType = ScreeningDocType;
  selectedFiles = new Map<ScreeningDocType, File>();
  draggingOver: ScreeningDocType | null = null;
  uploading = false;
  error = '';
  certificateTemplateUrl = '';
  certificateGuideUrl = '';

  constructor(
    private screeningService: TutorScreeningService,
    private dialogRef: MatDialogRef<ScreeningUploadDialogComponent>,
    private translateService: TranslateService,
  ) {
    this.translateService.get([
      'links.Screening_Certificate_Template_New',
      'links.Screening_Certificate_Guide_New',
    ]).subscribe(links => {
      this.certificateTemplateUrl = links['links.Screening_Certificate_Template_New'];
      this.certificateGuideUrl = links['links.Screening_Certificate_Guide_New'];
    });
  }

  close() {
    this.onClose.emit();
    this.dialogRef.close();
  }

  get titleKey(): string {
    return this.reminder ? 'texts.Screening_Reminder_Dialog_Title' : 'texts.Screening_Dialog_Title';
  }

  get introKey(): string {
    return this.reminder ? 'texts.Screening_Reminder_Dialog_Intro' : 'texts.Screening_Dialog_Intro';
  }

  get submitKey(): string {
    return this.reminder ? 'texts.Screening_Reminder_Dialog_Submit' : 'texts.Screening_Dialog_Submit';
  }

  docLabel(docType: ScreeningDocType): string {
    return docType === ScreeningDocType.Certificate
      ? 'texts.Screening_Certificate_Label'
      : 'texts.Screening_Qualification_Label';
  }

  docHint(docType: ScreeningDocType): string {
    return docType === ScreeningDocType.Certificate
      ? 'texts.Screening_Certificate_Hint_Online'
      : 'texts.Screening_Qualification_Hint';
  }

  onFileSelected(docType: ScreeningDocType, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.acceptFile(docType, input.files?.[0]);
    input.value = '';
  }

  onDragOver(docType: ScreeningDocType, event: DragEvent): void {
    event.preventDefault();
    if (!this.uploading) {
      this.draggingOver = docType;
    }
  }

  onDragLeave(docType: ScreeningDocType): void {
    if (this.draggingOver === docType) {
      this.draggingOver = null;
    }
  }

  onDrop(docType: ScreeningDocType, event: DragEvent): void {
    event.preventDefault();
    this.draggingOver = null;
    if (!this.uploading) {
      this.acceptFile(docType, event.dataTransfer?.files?.[0]);
    }
  }

  private acceptFile(docType: ScreeningDocType, file: File | undefined): void {
    if (!file) {
      return;
    }
    if (file.type !== 'application/pdf') {
      this.error = 'texts.Screening_Invalid_Format';
      this.selectedFiles.delete(docType);
      return;
    }
    if (file.size > ScreeningUploadDialogComponent.MAX_FILE_BYTES) {
      this.error = 'texts.Screening_File_Too_Large';
      this.selectedFiles.delete(docType);
      return;
    }
    this.selectedFiles.set(docType, file);
    this.error = '';
  }

  get canSubmit(): boolean {
    return !this.uploading && this.docTypes.every(dt => this.selectedFiles.has(dt));
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.uploading = true;
    this.error = '';

    this.screeningService.submitScreeningDocuments(this.selectedFiles)
      .pipe(finalize(() => (this.uploading = false)))
      .subscribe({
        next: () => {
          this.onConfirm.emit();
          this.dialogRef.close();
        },
        error: () => {
          this.error = 'texts.Screening_Upload_Error';
        },
      });
  }
}
